/*
 * KAN-736 — Marketing opt-in inside Sesami's "Review & Book" step.
 *
 * Sesami renders the booking modal from its own app bundle inside nested
 * (open) shadow roots, so the theme cannot template it. This script finds the
 * instant-booking page while the modal is open, appends an opt-in block under
 * the Phone field, and — only after Sesami reports a successful booking —
 * subscribes the booker to the ticked channels via Klaviyo's public client
 * subscriptions API.
 *
 * Fails closed: if Sesami changes its markup the block simply does not appear,
 * the booking is never blocked, and nothing is sent unless a box was ticked.
 */
(function () {
  'use strict';

  var configEl = document.getElementById('sesami-marketing-opt-in-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }
  if (!config) return;

  // Without a list there is nowhere to send consent, so the live theme hides
  // the block. Unpublished previews still show it (sending nothing) so the
  // brand can review the copy before a list is chosen.
  var theme = window.Shopify && window.Shopify.theme;
  var isLiveTheme = !theme || theme.role === 'main';
  if (!config.listId && isLiveTheme) return;

  var BLOCK_ID = 'olf-booking-opt-in';
  var STYLE_ID = 'olf-booking-opt-in-style';
  var PAGE_TAG = 'sesami-instant-booking-page';
  var KLAVIYO_REVISION = '2026-07-15';
  var POLL_MS = 300;
  var POLL_BURST_MS = 10 * 60 * 1000;

  var pollTimer = null;
  var pollStartedAt = 0;
  // One block per booking page, so a Sesami re-render re-attaches the same
  // inputs (keeping the customer's choices) instead of rebuilding defaults.
  var blocksByPage = new WeakMap();
  // The page whose Book button (or Enter key) was last used. Consent is only
  // ever read from this page, never from another widget on the same page.
  var submittedPage = null;

  function companyId() {
    var modules = window.klaviyoModulesObject;
    if (modules && modules.companyId) return modules.companyId;
    var script = document.querySelector('script[src*="klaviyo.com"][src*="company_id="]');
    if (!script) return null;
    var match = script.src.match(/company_id=([A-Za-z0-9]+)/);
    return match ? match[1] : null;
  }

  // Every match in the light DOM and in every open shadow root.
  function findAllDeep(root, tagName, found) {
    found = found || [];
    root.querySelectorAll(tagName).forEach(function (node) { found.push(node); });
    root.querySelectorAll('*').forEach(function (node) {
      if (node.shadowRoot) findAllDeep(node.shadowRoot, tagName, found);
    });
    return found;
  }

  function el(tag, attrs, text) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (key) {
      node.setAttribute(key, attrs[key]);
    });
    if (text) node.textContent = text;
    return node;
  }

  function checkboxRow(id, label, checked, describedBy) {
    var row = el('label', { class: 'olf-opt-in__row', for: id });
    var input = el('input', { type: 'checkbox', id: id, name: id });
    if (describedBy) input.setAttribute('aria-describedby', describedBy);
    input.checked = checked;
    row.appendChild(input);
    row.appendChild(el('span', {}, label));
    return { row: row, input: input };
  }

  function disclosure(id) {
    var p = el('p', { class: 'olf-opt-in__disclosure', id: id }, config.disclosure + ' ');
    var links = [];
    if (config.termsUrl) links.push(el('a', { href: config.termsUrl, target: '_blank', rel: 'noopener' }, 'Terms'));
    if (config.privacyUrl) links.push(el('a', { href: config.privacyUrl, target: '_blank', rel: 'noopener' }, 'Privacy Policy'));
    links.forEach(function (link, index) {
      if (index > 0) p.appendChild(document.createTextNode(' & '));
      p.appendChild(link);
    });
    if (links.length) p.appendChild(document.createTextNode('.'));
    return p;
  }

  var STYLES =
    '#' + BLOCK_ID + '{display:block;margin:4px 0 8px;padding:0;border:0;font:inherit;color:inherit}' +
    '#' + BLOCK_ID + ' legend{display:block;padding:0;margin:0 0 8px;font:inherit;font-size:16px}' +
    '#' + BLOCK_ID + ' .olf-opt-in__row{display:flex;align-items:center;gap:10px;margin:0 0 6px;font-size:16px;cursor:pointer}' +
    '#' + BLOCK_ID + ' input[type=checkbox]{display:inline-block;width:18px;height:18px;margin:0;flex:0 0 auto;accent-color:#333;cursor:pointer}' +
    '#' + BLOCK_ID + ' .olf-opt-in__disclosure{display:block;margin:8px 0 0;font-size:12px;line-height:1.45;color:#5d5d5d}' +
    '#' + BLOCK_ID + ' .olf-opt-in__disclosure a{color:inherit;text-decoration:underline}';

  function buildBlock() {
    var disclosureId = BLOCK_ID + '-disclosure';
    var block = el('fieldset', { id: BLOCK_ID });
    block.appendChild(el('legend', {}, config.heading));
    var email = checkboxRow(BLOCK_ID + '-email', config.emailLabel, config.emailDefaultChecked, null);
    var sms = checkboxRow(BLOCK_ID + '-sms', config.smsLabel, false, disclosureId);
    block.appendChild(email.row);
    block.appendChild(sms.row);
    block.appendChild(disclosure(disclosureId));
    return { node: block, email: email.input, sms: sms.input };
  }

  function injectInto(page) {
    if (!page.shadowRoot) return;
    var form = page.shadowRoot.querySelector('.form');
    if (!form) return;
    var block = blocksByPage.get(page);
    if (block && block.node.parentNode === form) return;

    if (!page.shadowRoot.getElementById(STYLE_ID)) {
      page.shadowRoot.appendChild(el('style', { id: STYLE_ID }, STYLES));
    }
    if (!block) {
      block = buildBlock();
      blocksByPage.set(page, block);
    }
    form.appendChild(block.node);
  }

  function inject() {
    findAllDeep(document, PAGE_TAG).forEach(injectInto);
  }

  // Book is pressed inside the page's shadow tree; composedPath() names the
  // page it came from. Must listen in the CAPTURE phase: Sesami stops the
  // click from bubbling to document (verified on the live modal 2026-09-22).
  function rememberSubmittedPage(event) {
    if (event.type === 'keydown' && event.key !== 'Enter') return;
    var path = event.composedPath ? event.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      if (path[i].tagName && path[i].tagName.toLowerCase() === PAGE_TAG) {
        if (blocksByPage.has(path[i])) submittedPage = path[i];
        return;
      }
    }
  }

  function stopPolling() {
    if (pollTimer) window.clearInterval(pollTimer);
    pollTimer = null;
  }

  function startPolling() {
    pollStartedAt = Date.now();
    if (pollTimer) return;
    pollTimer = window.setInterval(function () {
      if (Date.now() - pollStartedAt > POLL_BURST_MS) {
        stopPolling();
        return;
      }
      inject();
    }, POLL_MS);
  }

  function e164(code, number) {
    var digits = String(number || '').replace(/\D/g, '');
    if (!digits) return null;
    var prefix = String(code || '').replace(/[^\d+]/g, '');
    if (!prefix) return null;
    if (prefix.charAt(0) !== '+') prefix = '+' + prefix;
    return prefix + digits;
  }

  function subscribe(profileAttributes) {
    var id = companyId();
    if (!config.listId) return Promise.resolve();
    if (!id) {
      console.warn('[KAN-736] Klaviyo company id not found; booking opt-in not sent.');
      return Promise.resolve();
    }
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: config.source,
          profile: { data: { type: 'profile', attributes: profileAttributes } }
        },
        relationships: { list: { data: { type: 'list', id: config.listId } } }
      }
    };
    return fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(id), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body),
      keepalive: true
    }).catch(function () {
      /* Consent capture must never surface an error on a completed booking. */
    });
  }

  function onBookingSuccess() {
    var page = submittedPage;
    submittedPage = null;
    var block = page && blocksByPage.get(page);
    if (!block) return;

    var email = String(page.email || '').trim();
    var phone = e164(page.phoneCode, page.phoneNumber);

    // Separate calls so an invalid phone number cannot cost the email consent.
    if (block.email.checked && email) {
      subscribe({ email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } });
    }
    if (block.sms.checked && phone) {
      var attributes = { phone_number: phone, subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } } };
      if (email) attributes.email = email;
      subscribe(attributes);
    }
  }

  window.addEventListener('sesami:modal:opened', startPolling);
  window.addEventListener('sesami:slot:selected', startPolling);
  window.addEventListener('sesami:modal:closed', stopPolling);
  window.addEventListener('sesami:booking:success', onBookingSuccess);
  document.addEventListener('click', rememberSubmittedPage, true);
  document.addEventListener('keydown', rememberSubmittedPage, true);
})();
